import type { GitClient } from "./git/GitClient.js";
import type { GenerationRecord, StoredPatch } from "./types.js";

export interface CommitOptions {
    cliVersion: string;
    generatorVersions: Record<string, string>;
}

export class ReplayCommitter {
    private git: GitClient;
    private outputDir: string;

    constructor(git: GitClient, outputDir: string) {
        this.git = git;
        this.outputDir = outputDir;
    }

    async commitGeneration(message: string, options?: CommitOptions): Promise<string> {
        await this.stageAll();

        if (!(await this.hasStagedChanges())) {
            return (await this.git.exec(["rev-parse", "HEAD"])).trim();
        }

        let fullMessage = `[fern-generated] ${message}\n\nGenerated by Fern`;

        if (options?.cliVersion) {
            fullMessage += `\nCLI Version: ${options.cliVersion}`;
        }

        if (options?.generatorVersions && Object.keys(options.generatorVersions).length > 0) {
            fullMessage += "\nGenerators:";
            for (const [name, version] of Object.entries(options.generatorVersions)) {
                fullMessage += `\n  - ${name}: ${version}`;
            }
        }

        await this.git.exec(["commit", "-m", fullMessage]);
        return (await this.git.exec(["rev-parse", "HEAD"])).trim();
    }

    async commitReplay(
        _patchCount: number,
        patches?: StoredPatch[],
        message?: string,
        options?: {
            buckets?: {
                applied: StoredPatch[];
                unresolved: StoredPatch[];
                absorbed: StoredPatch[];
            };
        }
    ): Promise<string> {
        await this.stageAll();

        if (!(await this.hasStagedChanges())) {
            return (await this.git.exec(["rev-parse", "HEAD"])).trim();
        }

        let fullMessage = message ?? `[fern-replay] Applied customizations`;

        const buckets = options?.buckets;
        if (buckets) {
            // Honest, bucketed body so the customer can tell at a glance
            // which patches truly applied, which need their attention via
            // `fern replay resolve`, and which the generator now emits
            // natively. Empty buckets are omitted.
            if (buckets.applied.length > 0) {
                fullMessage += `\n\nPatches applied (${buckets.applied.length}):`;
                for (const p of buckets.applied) {
                    fullMessage += `\n  - ${p.id}: ${p.original_message}`;
                }
            }
            if (buckets.unresolved.length > 0) {
                fullMessage += `\n\nPatches with unresolved conflicts (${buckets.unresolved.length}):`;
                for (const p of buckets.unresolved) {
                    fullMessage += `\n  - ${p.id}: ${p.original_message}`;
                }
                fullMessage += `\n    Run \`fern-replay resolve\` to apply these customizations.`;
            }
            if (buckets.absorbed.length > 0) {
                fullMessage += `\n\nPatches absorbed by generator (${buckets.absorbed.length}):`;
                for (const p of buckets.absorbed) {
                    fullMessage += `\n  - ${p.id}: ${p.original_message}`;
                }
                fullMessage += `\n    The generator now produces these customizations natively.`;
            }
        } else if (patches && patches.length > 0) {
            // Legacy flat-list path (used by `commands/resolve.ts` after
            // hand-resolution and by tests that don't pass buckets). Every
            // patch is presumed applied at this point.
            fullMessage += "\n\nPatches replayed:";
            for (const patch of patches) {
                fullMessage += `\n  - ${patch.id}: ${patch.original_message}`;
            }
        }

        await this.git.exec(["commit", "-m", fullMessage]);
        return (await this.git.exec(["rev-parse", "HEAD"])).trim();
    }

    async createGenerationRecord(options?: CommitOptions): Promise<GenerationRecord> {
        const commitSha = (await this.git.exec(["rev-parse", "HEAD"])).trim();
        const treeHash = await this.getTreeHash(commitSha);

        return {
            commit_sha: commitSha,
            tree_hash: treeHash,
            timestamp: new Date().toISOString(),
            cli_version: options?.cliVersion ?? "unknown",
            generator_versions: options?.generatorVersions ?? {},
        };
    }

    async stageAll(): Promise<void> {
        await this.git.exec(["add", "-A", this.outputDir]);
    }

    async hasStagedChanges(): Promise<boolean> {
        const output = await this.git.exec(["diff", "--cached", "--name-only"]);
        return output.trim().length > 0;
    }

    async getTreeHash(commitSha: string): Promise<string> {
        return this.git.getTreeHash(commitSha);
    }
}
